import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Card } from '@tremor/react';
import { getVAPorVerba, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /va-por-verba?dias=N) ─────────────────────────────

interface VerbaPerfil {
  verba: string;
  casos: number;
  num: number;
  den: number;
  taxa_real: number | null;
  def: number;
  alta: number;
  nao_def: number;
  base: number;
  valor: number;
  falt: number;
  assinatura: string;
  pct_validos: number | null;
}

interface VAPorVerbaData {
  disponivel: boolean;
  erro?: string;
  dias: number;
  casos_varridos: number;
  validos: number;
  verbas: VerbaPerfil[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const PERIODOS = [7, 14, 21];

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

// chip de assinatura: nao_deferida=super-inclusão=vermelho; valor=amarelo;
// faltante=azul; base=roxo (mapeamento pedido por Diego).
const CHIP: Record<string, { label: string; cls: string }> = {
  nao_deferida: {
    label: 'não-deferida',
    cls: 'bg-red-500/15 text-red-300 ring-red-500/30',
  },
  valor: {
    label: 'valor',
    cls: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  },
  faltante: {
    label: 'faltante',
    cls: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
  },
  base: {
    label: 'base',
    cls: 'bg-purple-500/15 text-purple-300 ring-purple-500/30',
  },
};

function AssinaturaChip({ tipo }: { tipo: string }) {
  const c = CHIP[tipo] ?? {
    label: tipo,
    cls: 'bg-slate-700/40 text-slate-300 ring-slate-600/40',
  };
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${c.cls}`}
    >
      {c.label}
    </span>
  );
}

export function VAPorVerbaPage() {
  const [dias, setDias] = useState(14);

  const { data, isLoading, error } = useQuery<VAPorVerbaData>({
    queryKey: ['va-por-verba', dias],
    queryFn: async () => (await getVAPorVerba(dias)).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar VA por verba')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const verbas = data?.verbas ?? [];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>VA por verba</Title>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDias(p)}
                className={`rounded px-2.5 py-1 text-xs transition ${
                  dias === p
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        <Text>
          Perfil de defeito POR VERBA medido pelo juiz-régua (nosso cálculo × o
          DISPOSITIVO real), NÃO R$-exato vs perito. Onde a qualidade dói, verba
          a verba: quantos casos têm defeito confirmado pelo 2º juiz e a
          assinatura dominante do defeito.
        </Text>
      </header>

      {!isLoading && errMsg && (
        <Card className="border border-red-800/40 bg-red-900/10">
          <Text className="text-red-300">{errMsg}</Text>
        </Card>
      )}

      {!isLoading && !errMsg && (
        <Text className="text-sm text-slate-400">
          {fmtInt(data?.validos)} casos válidos varridos de{' '}
          {fmtInt(data?.casos_varridos)} (janela de {dias} dias).
        </Text>
      )}

      <Card>
        <Title>Defeito confirmado por verba</Title>
        <Text className="text-xs text-slate-400">
          Ordenado por TAXA REAL (desc). Taxa real = casos com defeito naquela
          classe / casos que COMPUTARAM a classe (não mais ÷ todos os válidos).
          Numerador e denominador na MESMA taxonomia (classe do juiz), MESMO
          universo (casos varridos-válidos da janela).
        </Text>

        {isLoading && <Text className="mt-3">carregando…</Text>}

        {!isLoading && !errMsg && verbas.length === 0 && (
          <Text className="mt-3 text-sm text-slate-400">
            sem defeitos confirmados no período
          </Text>
        )}

        {!isLoading && !errMsg && verbas.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-4 font-medium">Verba (classe)</th>
                  <th className="py-2 pr-4 text-right font-medium">
                    Taxa real
                  </th>
                  <th className="py-2 pr-4 text-right font-medium">
                    Defeito / c/ a verba
                  </th>
                  <th className="py-2 pr-4 text-right font-medium">
                    % dos válidos
                  </th>
                  <th className="py-2 pr-4 text-right font-medium">
                    Sev. alta
                  </th>
                  <th className="py-2 pr-4 text-right font-medium">Defeitos</th>
                  <th className="py-2 pr-4 font-medium">Assinatura</th>
                </tr>
              </thead>
              <tbody>
                {verbas.map((v) => (
                  <tr
                    key={v.verba}
                    className="border-b border-slate-900 text-slate-200 hover:bg-slate-800/30"
                  >
                    <td className="py-2 pr-4 font-medium">{v.verba}</td>
                    <td className="py-2 pr-4 text-right font-semibold tabular-nums">
                      {v.taxa_real === null ? (
                        <span
                          className="text-slate-500"
                          title="sem casos que computaram esta classe (denominador=0)"
                        >
                          n/a
                        </span>
                      ) : (
                        <span className="text-amber-200">
                          {fmtPct(v.taxa_real)}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-300">
                      <span title="processos c/ defeito nesta classe / processos que computaram a classe (mesmo universo válido)">
                        {fmtInt(v.num)}
                        <span className="text-slate-500"> de </span>
                        {v.den > 0 ? fmtInt(v.den) : '—'}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-500">
                      {fmtPct(v.pct_validos)}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {v.alta > 0 ? (
                        <span className="text-red-300">{fmtInt(v.alta)}</span>
                      ) : (
                        <span className="text-slate-500">0</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-400">
                      {fmtInt(v.def)}
                    </td>
                    <td className="py-2 pr-4">
                      <AssinaturaChip tipo={v.assinatura} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Legenda/caveats OBRIGATÓRIOS (transparência) */}
      <Card className="border border-slate-800 bg-slate-900/30">
        <Text className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Como ler (caveats)
        </Text>
        <ul className="mt-2 space-y-1.5 text-xs text-slate-400">
          <li>
            <span className="font-medium text-slate-300">Fonte</span> = juiz-régua
            (nosso cálculo × DISPOSITIVO), NÃO R$-exato vs perito.
          </li>
          <li>
            <span className="font-medium text-slate-300">Taxa real</span> =
            processos DISTINTOS com defeito confirmado na classe ÷ processos que
            COMPUTARAM a classe (denominador ={' '}
            <code className="text-slate-300">verba_transacao</code>). Num e den na
            MESMA classe (taxonomia do juiz-régua) e MESMO universo (varridos-
            válidos da janela) → comparação maçã-com-maçã.
          </li>
          <li>
            <span className="font-medium text-slate-300">Denominador</span> =
            verbas extraídas/classificadas (
            <code className="text-slate-300">verba_transacao</code>), proxy de
            &quot;verba em jogo&quot; — pode diferir do cálculo final (uma verba
            classificada pode não sair no PDF, ou vice-versa).
          </li>
          <li>
            <span className="font-medium text-slate-300">
              Taxa real = n/a
            </span>{' '}
            → a classe não tem chave em <code className="text-slate-300">
              verba_transacao
            </code>{' '}
            (ex.: CORRECAO_JUROS, INSS, MULTA_FGTS_40 — são incidências/reflexos,
            não verba própria) → denominador 0.
          </li>
          <li>
            <span className="font-medium text-slate-300">% dos válidos</span>{' '}
            (coluna cinza, legada) = casos c/ defeito ÷ TODOS os válidos varridos
            — subestima; mantida só p/ referência.
          </li>
          <li>
            <span className="font-medium text-slate-300">
              &apos;não-deferida&apos;
            </span>{' '}
            (super-inclusão) pode ter falso-positivo por texto truncado.
          </li>
          <li className="pt-1 text-slate-500">
            Chips: não-deferida (super-inclusão) · valor · faltante · base.
          </li>
        </ul>
      </Card>
    </div>
  );
}
